import { createContext } from "react";
import { HotkeysProvider } from "react-hotkeys-hook";
import { useSelector } from "react-redux";

import AuthGate from "shared/components/AuthGate";
import AuthUserGate from "shared/components/AuthUserGate";
import { GateProps } from "shared/components/GateProps";
import StainGate from "shared/components/StainGate";
import useInteractionListener from "shared/hooks/useInteractionListener";
import {
  selectMfaSetupRequired,
  selectIsAccessTokenExpired,
  selectIsRefreshTokenExpired,
  selectLongLivedToken,
} from "shared/reducers/authReducer";
import { initializeSharedIcons } from "shared/utils/initializeSharedIcons";

import AppError from "AppError";
import AppRoutes, { LongLivedTokenRoutes } from "AppRoutes";
import AppSpinner from "AppSpinner";
import MacroCodeGate from "components/gates/MacroCodeGate";
import SocketMessageProvider from "components/gates/SocketMessageProvider";
import WebSocketGate from "components/gates/WebSocketGate";
import MfaMethodSelectionPage from "components/pages/MfaMethodSelectionPage";
import LoginRoutes from "LoginRoutes";

import ImpersonationBanner from "./components/ImpersonationBanner";

initializeSharedIcons();

export const SocketMessageContext = createContext<{
  socketMessage?: string;
  setSocketMessage: Function;
}>({ socketMessage: undefined, setSocketMessage: () => {} });

const App = () => {
  const isAccessTokenExpired = useSelector(selectIsAccessTokenExpired);
  const isRefreshTokenExpired = useSelector(selectIsRefreshTokenExpired);
  const mfaSetupRequired = useSelector(selectMfaSetupRequired);
  const longLivedToken = useSelector(selectLongLivedToken);

  useInteractionListener();

  if (isAccessTokenExpired && isRefreshTokenExpired) return <LoginRoutes />;

  if (mfaSetupRequired) return <MfaMethodSelectionPage />;

  const gateProps: Omit<GateProps, "children"> = {
    LoadingEl: AppSpinner,
    ErrorEl: AppError,
  };

  if (longLivedToken) {
    // This is exclusively used by the Report Generator
    return (
      <AuthGate {...gateProps}>
        <StainGate LoadingEl={() => <></>} ErrorEl={AppError} justStains>
          <LongLivedTokenRoutes />
        </StainGate>
      </AuthGate>
    );
  }

  return (
    <AuthGate {...gateProps}>
      <AuthUserGate {...gateProps}>
        <SocketMessageProvider>
          <WebSocketGate />
          <MacroCodeGate {...gateProps}>
            <StainGate {...gateProps}>
              <HotkeysProvider initiallyActiveScopes={["viewerHotkeys"]}>
                <ImpersonationBanner />
                <AppRoutes />
              </HotkeysProvider>
            </StainGate>
          </MacroCodeGate>
        </SocketMessageProvider>
      </AuthUserGate>
    </AuthGate>
  );
};

export default App;
